import React, { type FC, useEffect } from 'react'
import SettingsIcon from '@mui/icons-material/Settings'

import {
  FormContainer,
  FormFieldsContainer,
  FormFieldTitle,
  FormHeader,
  FormInput,
  FormInputContainer
} from './CustomersView.style'
import Divider from '@mui/material/Divider'
import { Button, Menu, MenuItem } from '@mui/material'

import SaveIcon from '@mui/icons-material/Save'
import { CustomerUsers } from './CustomerUsers'
import { useTranslation } from 'react-i18next'
import {
  CreateCompanyAction,
  DeleteCompanyAction,
  SetCompanyEditModeAction,
  UpdateCompanyAction
} from '../../../redux/admin/admin-companies-actions'
import { type RootState } from '../../../redux/store'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import { type SelectedOrganization } from '../../../redux/admin/admin-companies-types'

interface CustomersDetailsProps {
  selectedOrganization: SelectedOrganization
}

export const CustomersDetails: FC<CustomersDetailsProps> = ({ selectedOrganization }: CustomersDetailsProps) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const editing = useAppSelector<boolean>((state: RootState) => state.admin.editCompanyDialog.editMode)

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [companyName, setCompanyName] = React.useState<string>('')
  const [companyTown, setCompanyTown] = React.useState<string>('')
  const [companyPostalCode, setCompanyPostalCode] = React.useState<string>('')
  const [companyStreetAddress, setCompanyStreetAddress] = React.useState<string>('')
  const [companyBusinessNumber, setCompanyBusinessNumber] = React.useState<string>('')

  const isCreatingNewCompany = selectedOrganization.id === '' && selectedOrganization.newCompany

  useEffect(() => {
    setCompanyName(selectedOrganization.name)
    setCompanyTown(selectedOrganization.town)
    setCompanyPostalCode(selectedOrganization.postalCode)
    setCompanyStreetAddress(selectedOrganization.streetAddress)
    setCompanyBusinessNumber(selectedOrganization.vatId)
  }, [selectedOrganization])

  // Validation for company saving comes here
  const canSaveCompany = (): boolean => {
    const canSave = companyName != null && companyName !== ''
    if (isCreatingNewCompany || editing) {
      return canSave
    }

    return false
  }

  const handleDetailsFormOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget)
  }

  const handleDetailsFormClose = (): void => {
    setAnchorEl(null)
  }

  const onEdit = (): void => {
    dispatch(SetCompanyEditModeAction({ editMode: true }))
  }

  const deleteCompanyPopup = (company: SelectedOrganization): void => {
    const msg: string = t('settings_dialog.delete_confirmation_short')
    if (window.confirm(msg)) {
      dispatch(DeleteCompanyAction({ companyId: company.id }))
    }
  }

  const saveCompany = (): void => {
    if (isCreatingNewCompany) {
      const msg: string = `${t('companies.confirmCreateCompany', { companyName })}`
      if (!window.confirm(msg)) {
        return
      }

      dispatch(
        CreateCompanyAction({
          name: companyName,
          town: companyTown,
          postalCode: companyPostalCode,
          streetAddress: companyStreetAddress,
          vatId: companyBusinessNumber

        })
      )
    } else {
      dispatch(
        UpdateCompanyAction({
          ...selectedOrganization,
          id: selectedOrganization.id,
          name: companyName,
          vatId: companyBusinessNumber,
          streetAddress: companyStreetAddress,
          postalCode: companyPostalCode,
          town: companyTown
        })
      )
    }

    // Finish edit mode
    dispatch(SetCompanyEditModeAction({ editMode: false }))
  }

  return (
        <FormContainer id={selectedOrganization.id}>
            <FormHeader>
                <FormFieldTitle>
                    {t('companyDetails.companyDetails')}{' '}
                    {editing ? `(${t('companyDetails.notSaved')}!)` : ''}
                </FormFieldTitle>
                <Button onClick={handleDetailsFormOpen} aria-label="edit">
                    <SettingsIcon color="action"/>
                </Button>
                <Menu
                    id="customers-details-form-menu"
                    anchorEl={anchorEl}
                    keepMounted
                    open={Boolean(anchorEl)}
                    onClose={handleDetailsFormClose}
                >
                    <MenuItem onClick={onEdit}>{t('edit')}</MenuItem>
                    <MenuItem onClick={() => {
                      deleteCompanyPopup(selectedOrganization)
                    }}>
                        {t('companyDetails.deleteCompany')}
                    </MenuItem>
                </Menu>
            </FormHeader>
            <FormFieldsContainer>
                <FormInputContainer>
                    <label htmlFor="name"> {t('companyDetails.companyName')}</label>
                    <FormInput
                        id="name"
                        name="name"
                        type="text"
                        variant="outlined"
                        value={companyName}
                        InputProps={{ disabled: !editing }}
                        error={companyName === ''}
                        onChange={(e) => {
                          setCompanyName(e.target.value)
                        }}
                    />
                </FormInputContainer>
                <FormInputContainer>
                    <label htmlFor="business-number">
                        {t('companyDetails.businessNumber')}
                    </label>
                    <FormInput
                        variant="outlined"
                        InputProps={{ disabled: !editing }}
                        type="text"
                        name="business-number"
                        id="business-number"
                        value={companyBusinessNumber}
                        onChange={(e) => {
                          setCompanyBusinessNumber(e.target.value)
                        }}
                    />
                </FormInputContainer>
            </FormFieldsContainer>
            <FormFieldsContainer>
                <FormInputContainer>
                    <label htmlFor="street-address">
                        {t('companyDetails.streetAddress')}
                    </label>
                    <FormInput
                        variant="outlined"
                        InputProps={{ disabled: !editing }}
                        type="text"
                        name="street-address"
                        id="street-address"
                        value={companyStreetAddress}
                        onChange={(e) => {
                          setCompanyStreetAddress(e.target.value)
                        }}
                    />
                </FormInputContainer>
            </FormFieldsContainer>
            <FormFieldsContainer>
                <FormInputContainer>
                    <label htmlFor="postal-code">
                        {t('companyDetails.postalCode')}
                    </label>
                    <FormInput
                        variant="outlined"
                        InputProps={{ disabled: !editing }}
                        type="text"
                        name="postal-code"
                        id="postal-code"
                        value={companyPostalCode}
                        onChange={(e) => {
                          setCompanyPostalCode(e.target.value)
                        }}
                    />
                </FormInputContainer>
                <FormInputContainer>
                    <label htmlFor="town">{t('companyDetails.town')}</label>
                    <FormInput
                        variant="outlined"
                        InputProps={{ disabled: !editing }}
                        type="text"
                        name="town"
                        id="town"
                        value={companyTown}
                        onChange={(e) => {
                          setCompanyTown(e.target.value)
                        }}
                    />
                </FormInputContainer>
            </FormFieldsContainer>
            <Divider/>
            {editing && (
                <FormFieldsContainer>
                    <Button
                        style={{ marginTop: '1rem', marginBottom: '1rem' }}
                        color="primary"
                        variant={'contained'}
                        disabled={!canSaveCompany()}
                        onClick={saveCompany}
                    >
                        <SaveIcon color="action"/>
                        {' '}
                        {t(isCreatingNewCompany ? 'companyDetails.createNew' : 'common.actions.save')}
                    </Button>
                </FormFieldsContainer>
            )}

            <CustomerUsers selectedOrganization={selectedOrganization}/>
        </FormContainer>
  )
}
